import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, Users, Loader2, RefreshCw } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CTC Group hierarchy picker used by Payroll Run.
 *
 * Renders a 3-level tree:
 *   Parent Group (Staff / O&M / Contract / ...)
 *     └─ Sub-Group (Plumbing / Tier-1 / ...)
 *         └─ Individual CTC Group (one card per structure)
 *
 * User can:
 *  - Tick a parent → ticks every CTC group beneath it
 *  - Tick a sub-group → ticks every CTC group beneath that sub-group
 *  - Tick individual leaves
 * The picker emits a flat list of selected `ctc_group_ids` (strings).
 *
 * If `processedGroupIds` is passed (groups already run for this period),
 * those leaves are shown with a green "Processed" pill and excluded from
 * the default selection — supports incremental batch runs (do 5 groups
 * now, 3 later, balance after).
 */
export default function CTCGroupTreePicker({ companyId, value = [], onChange,
                                              processedGroupIds = [] }) {
  const [tree, setTree] = useState([]);
  const [unassigned, setUnassigned] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openParents, setOpenParents] = useState(new Set());
  const [openSubs, setOpenSubs] = useState(new Set());
  const [hideProcessed, setHideProcessed] = useState(false);
  const selected = useMemo(() => new Set(value), [value]);
  const processed = useMemo(() => new Set(processedGroupIds), [processedGroupIds]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/controls/ctc-groups/tree?company_id=${companyId}`,
        { withCredentials: true });
      setTree(data.tree || []);
      setUnassigned(data.total_unassigned || 0);
      // Auto-expand the first 2 parents
      setOpenParents(new Set((data.tree || []).slice(0, 2).map(p => p.name)));
    } catch (e) { /* non-fatal */ }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (set, key) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  };

  const idsUnderParent = (parent) =>
    parent.sub_groups.flatMap(sg => sg.ctc_groups.flatMap(g => processed.has(g.id) ? [] : [g.id]));
  const idsUnderSub = (sub) =>
    sub.ctc_groups.flatMap(g => processed.has(g.id) ? [] : [g.id]);

  const setMany = (ids, checked) => {
    const next = new Set(selected);
    ids.forEach(id => checked ? next.add(id) : next.delete(id));
    onChange(Array.from(next));
  };

  const totalSelected = selected.size;
  const totalEmployees = useMemo(() => {
    let n = 0;
    tree.forEach(p => p.sub_groups.forEach(s => s.ctc_groups.forEach(g => {
      if (selected.has(g.id)) n += g.employee_count;
    })));
    return n;
  }, [tree, selected]);
  const totalProcessed = processed.size;

  const allLeafIds = useMemo(() =>
    tree.flatMap(p => p.sub_groups.flatMap(s => s.ctc_groups.flatMap(g => processed.has(g.id) ? [] : [g.id]))),
    [tree, processed]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="ctc-tree-picker">
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
        <Users size={14} className="text-slate-600" />
        <span className="text-xs font-semibold text-slate-700">CTC Group Selector</span>
        <span className="ml-auto text-[11px] text-slate-600">
          <b className="text-blue-700">{totalSelected}</b> picked ·
          <b className="text-emerald-700"> {totalEmployees}</b> employees
          {totalProcessed > 0 && <> · <b className="text-emerald-600">{totalProcessed}</b> already processed</>}
        </span>
        <button onClick={() => onChange(allLeafIds)}
          data-testid="ctc-tree-select-all"
          className="text-[11px] text-blue-600 hover:underline">
          Select all pending
        </button>
        <span className="text-slate-300">|</span>
        <button onClick={() => onChange([])}
          data-testid="ctc-tree-clear"
          className="text-[11px] text-slate-600 hover:underline">Clear</button>
        {totalProcessed > 0 && (
          <>
            <span className="text-slate-300">|</span>
            <label className="text-[11px] text-slate-600 flex items-center gap-1">
              <input type="checkbox" checked={hideProcessed}
                onChange={e => setHideProcessed(e.target.checked)} />
              Hide processed
            </label>
          </>
        )}
        <button onClick={load} className="text-slate-500 hover:text-slate-700"><RefreshCw size={11} /></button>
      </div>

      {loading ? (
        <div className="p-6 text-center text-slate-500 text-xs"><Loader2 className="animate-spin inline mr-1" size={12} />Loading…</div>
      ) : tree.length === 0 ? (
        <div className="p-6 text-center text-slate-500 text-xs">
          No CTC groups in this company yet — create them under Business Controls → Salary Structures (CTC Groups).
        </div>
      ) : (
        <div className="max-h-[400px] overflow-y-auto">
          {tree.map(parent => {
            const parentIds = idsUnderParent(parent);
            const allParentChecked = parentIds.length > 0 && parentIds.every(id => selected.has(id));
            const someParentChecked = parentIds.some(id => selected.has(id)) && !allParentChecked;
            const isParentOpen = openParents.has(parent.name);
            return (
              <div key={parent.name} className="border-b border-slate-100">
                <div className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                  onClick={() => setOpenParents(toggle(openParents, parent.name))}>
                  {isParentOpen ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
                  <input type="checkbox" checked={allParentChecked}
                    ref={el => el && (el.indeterminate = someParentChecked)}
                    onClick={e => e.stopPropagation()}
                    onChange={e => setMany(parentIds, e.target.checked)}
                    data-testid={`ctc-tree-parent-${parent.name.replace(/\s+/g, '-')}`}
                    className="rounded border-slate-300" />
                  <span className="font-semibold text-sm text-slate-800">{parent.name}</span>
                  <span className="text-[10px] text-slate-500">
                    {parent.ctc_count} structure{parent.ctc_count !== 1 ? 's' : ''} · {parent.employee_count} emp
                  </span>
                </div>
                {isParentOpen && parent.sub_groups.map(sub => {
                  const subKey = `${parent.name}>${sub.name}`;
                  const subIds = idsUnderSub(sub);
                  const allSubChecked = subIds.length > 0 && subIds.every(id => selected.has(id));
                  const someSubChecked = subIds.some(id => selected.has(id)) && !allSubChecked;
                  const isSubOpen = openSubs.has(subKey);
                  const showSubLabel = sub.name !== '—';
                  return (
                    <div key={sub.name} className={showSubLabel ? 'pl-6' : ''}>
                      {showSubLabel && (
                        <div className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer"
                          onClick={() => setOpenSubs(toggle(openSubs, subKey))}>
                          {isSubOpen ? <ChevronDown size={12} className="text-slate-400" /> : <ChevronRight size={12} className="text-slate-400" />}
                          <input type="checkbox" checked={allSubChecked}
                            ref={el => el && (el.indeterminate = someSubChecked)}
                            onClick={e => e.stopPropagation()}
                            onChange={e => setMany(subIds, e.target.checked)}
                            data-testid={`ctc-tree-sub-${parent.name}-${sub.name}`.replace(/\s+/g, '-')}
                            className="rounded border-slate-300" />
                          <span className="text-xs font-medium text-slate-700">{sub.name}</span>
                          <span className="text-[10px] text-slate-500">{sub.employee_count} emp</span>
                        </div>
                      )}
                      {(!showSubLabel || isSubOpen) && (
                        <div className={showSubLabel ? 'pl-6' : 'pl-3'}>
                          {sub.ctc_groups
                            .filter(g => !hideProcessed || !processed.has(g.id))
                            .map(g => {
                            const isProcessed = processed.has(g.id);
                            return (
                              <div key={g.id} className={`flex items-center gap-2 px-3 py-1 ${isProcessed ? 'bg-emerald-50/40 opacity-60' : 'hover:bg-blue-50'}`}>
                                <input type="checkbox"
                                  checked={!isProcessed && selected.has(g.id)}
                                  disabled={isProcessed}
                                  onChange={e => setMany([g.id], e.target.checked)}
                                  data-testid={`ctc-tree-leaf-${g.code}`}
                                  className="rounded border-slate-300 disabled:opacity-50" />
                                <span className="font-mono text-[11px] text-blue-700">{g.code}</span>
                                <span className="text-xs text-slate-700 flex-1">{g.name}</span>
                                {isProcessed && <span className="text-[9px] bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded font-semibold">✓ Processed</span>}
                                {g.ot_enabled && <span className="text-[9px] bg-amber-100 text-amber-700 px-1 rounded">OT</span>}
                                {g.pay_cycle_label && <span className="text-[9px] bg-violet-100 text-violet-800 px-1 rounded font-semibold" title={`Pay cycle ${g.pay_cycle_start_day}–${g.pay_cycle_end_day}`}>Cycle {g.pay_cycle_label}</span>}
                                <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1 rounded">{g.base_days_fixed}d</span>
                                <span className="text-[11px] text-slate-500 w-12 text-right">{g.employee_count} emp</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {unassigned > 0 && (
        <div className="bg-amber-50 border-t border-amber-200 px-3 py-2 text-[11px] text-amber-800">
          ⚠️ <b>{unassigned} employee{unassigned !== 1 ? 's' : ''}</b> not yet assigned to any CTC group — they will be EXCLUDED from this run if you use a group filter.
          <a href="/employees" className="ml-2 text-amber-900 underline">Assign now →</a>
        </div>
      )}
    </div>
  );
}
